import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded';
import EducatorLayout from './components/EducatorLayout';
import api from '../api/api';
import { AuthContext } from '../auth/AuthContext';

const locales = { 'en-US': require('date-fns/locale/en-US') };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const JOIN_EARLY_MIN = 5;

const MyTimetable = () => {
  const { user, token } = useContext(AuthContext) ?? {};
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [joinError, setJoinError] = useState(null);
  const [joiningId, setJoiningId] = useState(null);
  const [calendarView, setCalendarView] = useState(Views.MONTH);
  const [calendarDate, setCalendarDate] = useState(new Date());

  useEffect(() => {
    const fetchSessions = async () => {
      if (!token || !user?._id) return;
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get('/sessions', {
          params: {
            educatorId: user._id,
            sort: 'startTime',
            limit: 200,
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSessions(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load timetable.');
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [token, user?._id]);

  const upcomingSessions = useMemo(() => {
    const now = Date.now();
    return (sessions || [])
      .filter((s) => {
        const start = new Date(s.startTime).getTime();
        const end = s.endTime ? new Date(s.endTime).getTime() : start + 65 * 60 * 1000;
        return end > now - 60 * 60 * 1000; // show current and near-future
      })
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [sessions]);

  const calendarEvents = useMemo(
    () =>
      upcomingSessions.map((s) => ({
        id: s._id,
        title: s.subject || s.title || 'Lesson',
        start: new Date(s.startTime),
        end: s.endTime ? new Date(s.endTime) : new Date(new Date(s.startTime).getTime() + 65 * 60 * 1000),
        joinUrl: s.joinUrl,
      })),
    [upcomingSessions]
  );

  const canJoin = (session) => {
    const now = Date.now();
    const start = new Date(session.startTime).getTime();
    const end = session.endTime ? new Date(session.endTime).getTime() : start + 65 * 60 * 1000;
    const minutesToStart = (start - now) / 60000;
    return minutesToStart <= JOIN_EARLY_MIN && now <= end;
  };

  const handleJoin = async (session) => {
    if (!session?._id || !token) return;
    setJoinError(null);
    setJoiningId(session._id);
    try {
      const { data } = await api.get(`/sessions/${session._id}/join`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = data?.url;
      if (!url) {
        throw new Error('Join URL not available.');
      }
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setJoinError(err?.response?.data?.message || err.message || 'Failed to join session.');
    } finally {
      setJoiningId(null);
    }
  };

  const renderSessionCard = (session) => {
    const start = new Date(session.startTime);
    const end = session.endTime ? new Date(session.endTime) : new Date(start.getTime() + 65 * 60 * 1000);
    const durationMin = Math.max(1, differenceInMinutes(end, start));
    const joinDisabled = !canJoin(session) || joiningId === session._id;
    const joinTitle = joinDisabled ? `Join opens ${JOIN_EARLY_MIN} minutes before start` : 'Join live class';

    return (
      <Grid item xs={12} md={6} lg={4} key={session._id}>
        <Card elevation={1} sx={{ height: '100%', borderRadius: 3 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <MeetingRoomRoundedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>
                {session.subject || session.title || 'Lesson'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
              <CalendarTodayRoundedIcon fontSize="small" />
              <Typography variant="body2">
                {start.toLocaleDateString()} - {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
              <AccessTimeRoundedIcon fontSize="small" />
              <Typography variant="body2">{durationMin} min</Typography>
            </Stack>
            {session.sectionId?.name && (
              <Chip size="small" label={session.sectionId.name} variant="outlined" sx={{ alignSelf: 'flex-start' }} />
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={joinTitle}>
              <span>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PlayArrowRoundedIcon />}
                  disabled={joinDisabled}
                  onClick={() => handleJoin(session)}
                >
 {joiningId === session._id ? 'Joining...' : 'Join Live Class'}
                </Button>
              </span>
            </Tooltip>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <EducatorLayout>
      <Box>
        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            {loading ? (
              <Stack alignItems="center" py={3}>
                <CircularProgress />
              </Stack>
 ) : error ? (
              <Alert severity="error">{error}</Alert>
 ) : (
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      My Timetable
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      View scheduled lessons and join your BigBlueButton class when it opens.
                    </Typography>
                  </Box>
                  <Chip
                    color="primary"
                    variant="outlined"
                    label={`Join window opens ${JOIN_EARLY_MIN} mins before start`}
                  />
                </Stack>

                {upcomingSessions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No lessons scheduled yet.
                  </Typography>
 ) : (
                  <Grid container spacing={2}>
                    {upcomingSessions.map(renderSessionCard)}
                  </Grid>
                )}
                {joinError && <Alert severity="error">{joinError}</Alert>}

                <Box sx={{ '& .rbc-calendar': { minHeight: 480 } }}>
                  <Calendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    view={calendarView}
                    date={calendarDate}
                    onView={setCalendarView}
                    onNavigate={setCalendarDate}
                    eventPropGetter={(event) => ({
                      style: {
                        backgroundColor: '#1a237e',
                        borderRadius: 6,
                      },
                    })}
                    style={{ height: 480, backgroundColor: 'white' }}
                  />
                </Box>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </EducatorLayout>
  );
};

export default MyTimetable;
